import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { TourCard } from "@/components/TourCard";
import { Button } from "@/components/ui/button";
import { Clock, Users, MapPin, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { LeadCapturePopup } from "@/components/LeadCapturePopup";
import { StarRating } from "@/components/StarRating";
import { useSEO } from "@/lib/seo";

export default function HomePage() {
  useSEO({
    title: null,
    description: "Book the best Saint Lucia tours and excursions with Keyz's Epic Adventure Tours. Luxury catamaran cruises, Pitons hikes, zip-lining, snorkeling, sunset sails, epic combos & kids packages. Top-rated tour operator - book online today!",
    image: "https://images.unsplash.com/photo-1605288132419-1aa5e25475b2?w=1200&q=80",
  });
  const [tours, setTours] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/tours"),
      api.get("/tours/featured").catch(() => ({ data: [] }))
    ]).then(([allRes, featRes]) => {
      const allTours = Array.isArray(allRes.data) ? allRes.data : [];
      const featTours = Array.isArray(featRes.data) ? featRes.data : [];
      setTours(allTours);
      setFeatured(featTours.length > 0 ? featTours : allTours.slice(0, 3));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  const categories = [
    { name: "Land", label: "Land Tours", desc: "Hike volcanic peaks & walk the rainforest canopy", image: "https://images.pexels.com/photos/238631/pexels-photo-238631.jpeg?auto=compress&cs=tinysrgb&w=800" },
    { name: "Sea", label: "Sea Excursions", desc: "Luxury catamarans & sunset sails", image: "https://images.unsplash.com/photo-1627761801957-4bf6cfb4fa20?w=800&q=80" },
    { name: "Adventure", label: "Adventure", desc: "Zip-lines, snorkeling & adrenaline", image: "https://images.pexels.com/photos/3041867/pexels-photo-3041867.jpeg?auto=compress&cs=tinysrgb&w=800" },
    { name: "Epic Combos", label: "Epic Combos", desc: "Multi-tour packages for the ultimate experience", image: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800&q=80" },
    { name: "Kids Package", label: "Kids Package", desc: "Family-friendly adventures for young explorers", image: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80" },
  ];

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />

      {/* Hero */}
      <section className="relative h-screen flex items-end pb-16 sm:pb-24" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1605288132419-1aa5e25475b2?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Saint Lucia Pitons"
            className="w-full h-full object-cover"
          />
          <div className="hero-gradient absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <p className="font-manrope uppercase text-xs tracking-[0.25em] text-sandy mb-4 opacity-0 animate-fade-up">
            Luxury Excursions in Saint Lucia
          </p>
          <h1 className="font-cormorant text-4xl sm:text-5xl lg:text-7xl font-light text-white leading-[1.1] mb-6 opacity-0 animate-fade-up stagger-1">
            Keyz's Epic<br />Adventure Tours
          </h1>
          <p className="text-white/80 font-manrope text-sm sm:text-base lg:text-lg max-w-lg mb-8 opacity-0 animate-fade-up stagger-2">
            Discover the uncharted beauty of Saint Lucia through our curated
            collection of extraordinary experiences.
          </p>
          <Link to="/tours" data-testid="hero-explore-tours-btn">
            <Button className="rounded-full bg-sandy text-midnight hover:bg-sandy/90 px-8 py-3 h-auto font-manrope font-medium text-sm tracking-wide opacity-0 animate-fade-up stagger-3 transition-transform duration-300 hover:-translate-y-0.5">
              Explore Tours
              <ChevronRight className="ml-1 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Categories Bento Grid */}
      <section className="py-16 sm:py-24 px-6 max-w-7xl mx-auto">
        <div className="mb-12">
          <p className="uppercase text-xs tracking-[0.25em] text-sandy font-manrope mb-3">Categories</p>
          <h2 className="font-cormorant text-3xl sm:text-4xl font-light text-midnight">Discover Your Adventure</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {categories.map((cat, i) => (
            <Link
              key={cat.name}
              to={`/tours?category=${cat.name}`}
              className={`group cursor-pointer overflow-hidden relative ${i === 0 ? 'sm:row-span-2 aspect-[3/4] sm:aspect-auto' : 'aspect-[4/3]'}`}
              data-testid={`category-${cat.name.toLowerCase().replace(/\s/g, '-')}`}
            >
              <img
                src={cat.image}
                alt={cat.label}
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="category-overlay absolute inset-0" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="uppercase text-xs tracking-[0.25em] text-sandy font-manrope mb-1">{cat.name}</p>
                <h3 className="font-cormorant text-2xl text-white mb-1">{cat.label}</h3>
                <p className="text-white/70 text-sm font-manrope">{cat.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Tours */}
      {!loading && featured.length > 0 && (
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="uppercase text-xs tracking-[0.25em] text-sandy font-manrope mb-3">Featured</p>
                <h2 className="font-cormorant text-3xl sm:text-4xl font-light text-midnight">Signature Experiences</h2>
              </div>
              <Link to="/tours" className="hidden sm:flex items-center text-sandy font-manrope text-sm hover:underline" data-testid="view-all-tours-link">
                View All <ChevronRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {featured.map((tour, i) => (
                <div key={tour.tour_id} className={`opacity-0 animate-fade-up stagger-${i + 1}`}>
                  <TourCard tour={tour} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Stats / Trust Section */}
      <section className="py-16 sm:py-20 bg-bone">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { icon: MapPin, value: "20+", label: "Unique Destinations" },
              { icon: Users, value: "5,000+", label: "Happy Guests" },
              { icon: Clock, value: "10+", label: "Years of Experience" },
              { icon: ChevronRight, value: "4.9", label: "Average Rating" },
            ].map((stat, i) => (
              <div key={i} className="flex flex-col items-center">
                <stat.icon className="w-5 h-5 text-sandy mb-3" />
                <p className="font-cormorant text-3xl sm:text-4xl font-light text-midnight">{stat.value}</p>
                <p className="font-manrope text-xs uppercase tracking-[0.2em] text-midnight/50 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1200&q=80"
            alt="Caribbean waters"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-midnight/70" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <p className="uppercase text-xs tracking-[0.25em] text-sandy font-manrope mb-4">Ready?</p>
          <h2 className="font-cormorant text-3xl sm:text-4xl lg:text-5xl font-light text-white mb-6">
            Your Saint Lucia Adventure Awaits
          </h2>
          <p className="text-white/70 font-manrope text-sm sm:text-base mb-8 max-w-xl mx-auto">
            Browse our collection of handpicked excursions and create memories that last a lifetime.
          </p>
          <Link to="/tours" data-testid="cta-explore-btn">
            <Button className="rounded-full bg-sandy text-midnight hover:bg-sandy/90 px-8 py-3 h-auto font-manrope font-medium text-sm tracking-wide transition-transform duration-300 hover:-translate-y-0.5">
              Browse All Tours
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      <LeadCapturePopup />
    </div>
  );
}
